import React from 'react';
import styled from 'styled-components';

import { RadiusOption } from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import { useGetCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { ConditionalLink } from 'src/utils/conditional-link';
import { Category } from '../categories-nav.types';
import { categoryCardFocusRing } from './category-card-focus';

const radiusOptions = {
  [RadiusOption.rounded]: '16px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

type CompactCardProps = {
  category: Category;
  style: RadiusOption;
  isMobileGrid: boolean;
};

export const LargeCard = ({ category, style, isMobileGrid }: CompactCardProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const { key, label, slug, customCategory, cardImage, isOutlet, suppressImage } = category;

  const image = suppressImage ? undefined : cardImage ?? getCategoryPhoto(dispensary, category);
  const imgixParams = {
    width: 268,
  };

  const href = useGetCustomCategoryLink(customCategory, slug, key);

  return (
    <ConditionalLink url={href}>
      <Anchor
        href={href}
        $radius={style}
        $isMobileGrid={isMobileGrid}
        data-testid='category-list-link'
        onClick={category.onClick}
      >
        <Label $isOutlet={isOutlet}>{label}</Label>

        <ImageWrapper>
          {!suppressImage && (
            <Image
              src={image}
              disableSrcSet
              imgixParams={imgixParams}
              htmlAttributes={{ alt: `${label} category image` }}
            />
          )}
        </ImageWrapper>
      </Anchor>
    </ConditionalLink>
  );
};

const Anchor = styled.a<{ $radius: RadiusOption; $isMobileGrid?: boolean }>`
  background: var(--dcm-categories-card-bg);
  border: 1px solid var(--dcm-categories-card-border-color);
  border-radius: ${({ $radius }) => radiusOptions[$radius]};
  color: var(--dcm-categories-card-text-color);
  display: block;
  font-size: 18px;
  font-weight: 700;
  line-height: ${22 / 18};
  padding: 16px;
  transition: box-shadow 0.1s;
  white-space: nowrap;
  width: ${({ $isMobileGrid }) => ($isMobileGrid ? '100%' : '146px')};

  ${categoryCardFocusRing}

  @media screen and (min-width: 600px) {
    font-size: 20px;
    line-height: ${24 / 20};
    padding: 20px 24px;
    width: 184px;
  }

  &:hover {
    box-shadow: var(--dcm-categories-card-hover-shadow);
  }
`;

const ImageWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 67px;
  justify-content: center;
  margin: 16px auto 0;
  overflow: hidden;

  @media screen and (min-width: 600px) {
    height: 77px;
    margin-top: 20px;
  }
`;

const Image = styled(Imgix)`
  display: block;
  max-height: 100%;
  max-width: 100%;
`;

const Label = styled.h3<{ $isOutlet?: boolean }>`
  margin: 0;
  ${({ $isOutlet }) =>
    $isOutlet &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;
